import React, { useCallback, useEffect, useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
// Barrel-Import, weil pages-title-matrix.test.tsx genau dieses Modul mockt
// (pages/Options/components/index.ts). Ein Direktimport von components/EinstellungenSeitenKopf
// umgeht den Mock und bricht den Titel-Vertrag.
import EinstellungenSeitenKopf from '../../../pages/Options/components';
import { MARKERS } from '../usageKeys';
import type { UsageDays } from '../types';
import { useUsageInsights } from '../hooks/useUsageInsights';
import { useUsageErrors } from '../hooks/useUsageErrors';
import { useUsageTrackingEnabled } from '../hooks/useTrack';
import { downloadUsageExport } from '../services/usageExportDownload';
import { userLogOut } from '../../../context/CommunicationProvider/authUser';
import { formatDay, rangeText } from '../services/usageFormat';
import Abschnitt from '../components/Abschnitt';
import UeberblickKacheln from '../components/UeberblickKacheln';
import SeitenTabelle from '../components/SeitenTabelle';
import VersionsvergleichAbschnitt from '../components/VersionsvergleichAbschnitt';
import FunktionenTabelle from '../components/FunktionenTabelle';
import WutUndTotklickTabellen from '../components/WutUndTotklickTabellen';
import WegeTabelle from '../components/WegeTabelle';
import ServeroperationenTabelle from '../components/ServeroperationenTabelle';
import LadeleistungTabelle from '../components/LadeleistungTabelle';
import AnteilsListen from '../components/AnteilsListen';
import FormulareUndSuche from '../components/FormulareUndSuche';
import FehlerAbschnitt from '../components/FehlerAbschnitt';

// RITA-1505 (Nutzungserfassung Phase 2c, Epic RITA-1496) — Auswertungsseite Einstellungen > Nutzung.
//
// CONTRACT: Der EinstellungenSeitenKopf wird in JEDEM Zustand montiert (auch ladend, leer und
// fehlerhaft) und die Seite rendert ohne Props. Nur so traegt sie den Vertrag der
// PAGE_TITLE_MATRIX (pages-title-matrix.test.tsx), die jede Seite isoliert rendert.
// CONTRACT: Datenzugriff ausschliesslich ueber useQuery + gql (in den beiden Hooks) — kein
// useLazyQuery, keine Subscription, kein client.query. Der Matrix-Test ersetzt das gesamte Modul
// '@apollo/client' durch einen dauerhaften Ladezustand.
// CONTRACT: Die Seite ist SUPERADMIN-only. Gate ist RequireSuperAdmin (pages.tsx
// superAdminOnly: true -> Routes.tsx); die Kachel steuert nur die Sichtbarkeit.
//
// Zwei Abfragen, nicht acht: operationLogPlugin.ts schreibt je Anfrage eine Audit-Zeile. Jeder
// Zeitraumwechsel erzeugt damit bewusst zwei weitere Zeilen (fetchPolicy 'network-only').
//
// Eigenerfassung der Seite: der page_view laeuft wie ueberall, die beiden Abfragen erscheinen
// selbst unter „Serveroperationen“, Zeitraum- und Export-Knopf tragen feste Marker. Der Export
// selbst wird NICHT erfasst — instrumentiert sind nur der Apollo-Link und der axios-Interceptor.

/** Platzhalter des Ladezustands. Bewusst ohne alte Zahlen: sie gehörten zum vorigen Zeitraum. */
function LadeGeruest() {
  return (
    <Box data-testid="nutzung-ladend">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2 }}>
        {[0, 1, 2, 3, 4, 5].map((stelle) => (
          <Paper key={stelle} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Skeleton width="60%" />
            <Skeleton height={36} />
          </Paper>
        ))}
      </Box>
      <Skeleton width={160} height={28} sx={{ mt: 4 }} />
      <Skeleton variant="rectangular" height={180} sx={{ mt: 1, borderRadius: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Kennzahlen werden geladen … Der Zeitraumwechsel bleibt bedienbar; alte Zahlen werden nicht angezeigt, bis neue
        vorliegen.
      </Typography>
    </Box>
  );
}

const ZEITRAEUME: UsageDays[] = [7, 30, 90];
const STANDARD_ZEITRAUM: UsageDays = 30;

export default function NutzungPage() {
  const [days, setDays] = useState<UsageDays>(STANDARD_ZEITRAUM);
  const [exportLaeuft, setExportLaeuft] = useState(false);
  const [exportFehler, setExportFehler] = useState<string | null>(null);

  const insights = useUsageInsights(days);
  const fehlerAbfrage = useUsageErrors(days);
  const erfassung = useUsageTrackingEnabled();

  const overview = insights.data?.usageOverview;
  const leer = Boolean(insights.data) && !overview?.dataUntil;
  // Zeitraumtext und Lueckenhinweis gehoeren zur ANTWORT, nicht zum gewaehlten Zeitraum: waehrend
  // des Ladens haelt Apollo (notifyOnNetworkStatusChange) die vorige Antwort — ein Kopf aus neuem
  // days und altem Fenster/Stand/Luecken waere genau die Mischung, die der Ladezustand bei den
  // Kennzahlen vermeidet (KI-Review !738).
  //
  // RITA-1523 (KI-Review !748): Ein NACHLADEN des Bündels (refetch bei veralteter Standauswahl,
  // gleicher Zeitraum) hält die Seite. Apollo behält dabei `data` und meldet networkStatus
  // `refetch` — demontierte Abschnitte verlören den Bildlauf und die Einmal-Sperre des
  // Versionsvergleichs, und nach dem Wiederaufbau liefe dieselbe abgelehnte Auswahl erneut ins
  // Nachladen. Der erste Ladevorgang, ein Zeitraumwechsel (`setVariables`) und der Neuversuch nach
  // einem Fehler zeigen weiter das Gerüst — auch dann, wenn Apollo zum Fehler noch eine frühere
  // Antwort hält: das Zwischenergebnis des refetch trägt den Fehler nicht mehr, ohne Gerüst
  // verschwände der Alert und die alten Zahlen stünden da wie ein Erfolg. Ob der letzte ABGESCHLOSSENE
  // Zustand ein Fehler war, merkt sich die Seite deshalb selbst.
  const [nachFehler, setNachFehler] = useState(false);
  useEffect(() => {
    if (!insights.loading) setNachFehler(insights.error !== undefined);
  }, [insights.loading, insights.error]);
  const nachladen = insights.networkStatus === NetworkStatus.refetch && insights.data !== undefined && !nachFehler;
  const laedt = insights.loading && !nachladen;
  const kopfdaten = !laedt ? overview : undefined;

  const handleZeitraum = (wert: UsageDays) => () => {
    if (wert !== days) {
      setDays(wert);
      setExportFehler(null);
    }
  };

  const handleExport = async () => {
    setExportLaeuft(true);
    setExportFehler(null);
    const ergebnis = await downloadUsageExport({ days });
    setExportLaeuft(false);
    if (ergebnis.ok) return;
    // Bei 401 meldet DIESE Seite ab (KI-Review !738): der Export-Dienst markiert den Sitzungsablauf
    // nur, er kennt die Anwendung nicht. Ohne Abmeldung endete der Export still — kein Download,
    // keine Meldung, keine Weiterleitung. Eine zusaetzliche Meldung waere Laerm auf dem Weg zum Login.
    if (ergebnis.sessionExpired) {
      userLogOut();
      return;
    }
    setExportFehler(ergebnis.message);
  };

  // errorPolicy 'none' laesst refetch() im Fehlerfall ablehnen — ohne catch meldete die
  // Fehlererfassung (errorCapture.ts) eine unbehandelte Ablehnung als Anwendungsfehler.
  const handleErneut = () => {
    insights.refetch().catch(() => undefined);
    fehlerAbfrage.refetch().catch(() => undefined);
  };

  // RITA-1523: Die Ständeliste des Versionsvergleichs steckt im Bündel. Lehnt der Server eine
  // Auswahl als veraltet ab (selectionStale), braucht der Abschnitt eine FRISCHE Liste — mit der
  // alten könnte er die abgelehnte Auswahl nur gegen sich selbst tauschen.
  //
  // Abhängigkeit ist `refetch` selbst, nicht das Ergebnisobjekt: Apollo gibt bei jedem Rendern ein
  // neues QueryResult zurück, `refetch` bleibt dagegen dieselbe Funktion. So ist der Rückruf stabil
  // und der Effekt im Abschnitt läuft nicht bei jedem Rendern der Seite erneut.
  const { refetch: insightsNeuLesen } = insights;
  const handleStaendeNeuLaden = useCallback(() => {
    insightsNeuLesen().catch(() => undefined);
  }, [insightsNeuLesen]);

  return (
    <>
      <EinstellungenSeitenKopf title="Nutzung" />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mr: 'auto' }}>
          Nutzung
        </Typography>

        <ToggleButtonGroup size="small" exclusive value={days} aria-label="Zeitraum">
          {ZEITRAEUME.map((wert) => (
            <ToggleButton
              key={wert}
              value={wert}
              onClick={handleZeitraum(wert)}
              data-usage={MARKERS.nutzungZeitraum}
              data-testid={`nutzung-zeitraum-${wert}`}
            >
              {wert} Tage
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Die Lese-API kennt keinen Umgebungsparameter: sie antwortet IMMER mit den Zahlen der
            Produktivumgebung (usage.graphql, Kopf). Der Chip sagt das, statt es offenzulassen. */}
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label="Produktion"
          title="Die Auswertung zeigt immer die Produktivumgebung — unabhängig davon, wo diese Oberfläche läuft."
        />
        {erfassung === false && (
          <Chip size="small" color="warning" variant="outlined" label="Erfassung: ausgeschaltet" />
        )}

        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={exportLaeuft}
          data-usage={MARKERS.nutzungExport}
          data-testid="nutzung-export"
          startIcon={exportLaeuft ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
        >
          {exportLaeuft ? 'Export läuft …' : 'Excel exportieren'}
        </Button>
      </Box>

      {kopfdaten && (
        <Typography variant="body2" color="text.secondary" data-testid="nutzung-zeitraumtext" sx={{ mb: 2 }}>
          {rangeText({
            days,
            dataFrom: kopfdaten.dataFrom,
            dataUntil: kopfdaten.dataUntil,
            missingDays: kopfdaten.missingDays,
          })}
        </Typography>
      )}

      {kopfdaten && kopfdaten.missingDays.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="nutzung-luecken">
          {kopfdaten.missingDays.length === 1
            ? `Für den ${formatDay(kopfdaten.missingDays[0])} liegt keine Verdichtung vor. Die Zahlen dieses Tages fehlen — eine Lücke ist kein Rückgang.`
            : `Für ${kopfdaten.missingDays.length} Tage im Fenster liegt keine Verdichtung vor. Deren Zahlen fehlen — eine Lücke ist kein Rückgang.`}
        </Alert>
      )}

      {exportFehler && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="nutzung-export-fehler"
          onClose={() => setExportFehler(null)}
        >
          {exportFehler}
        </Alert>
      )}

      {laedt && <LadeGeruest />}

      {!laedt && insights.error && (
        <Alert
          severity="error"
          data-testid="nutzung-fehler"
          action={
            <Button color="inherit" size="small" onClick={handleErneut} data-testid="nutzung-erneut">
              Erneut versuchen
            </Button>
          }
        >
          <AlertTitle>Die Nutzungsdaten konnten nicht geladen werden.</AlertTitle>
          {insights.error.message}
        </Alert>
      )}

      {!laedt && !insights.error && leer && (
        <Paper
          variant="outlined"
          data-testid="nutzung-leer"
          sx={{ p: 5, borderRadius: 2, borderStyle: 'dashed', textAlign: 'center' }}
        >
          <Typography variant="h6" gutterBottom>
            Noch keine ausgewerteten Daten.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70ch', mx: 'auto' }}>
            Die Verdichtung läuft täglich in der Nacht und zeigt Daten ab dem ersten vollständigen Tag. Ist die
            Erfassung in dieser Umgebung ausgeschaltet, bleibt die Seite leer.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {erfassung === false && <Chip size="small" color="warning" label="Erfassung: ausgeschaltet" />}
            <Chip
              size="small"
              variant="outlined"
              label={`Letzte Verdichtung: ${overview?.lastRollupAt ? formatDay(overview.lastRollupAt.slice(0, 10)) : 'noch nie'}`}
            />
          </Box>
        </Paper>
      )}

      {!laedt && !insights.error && !leer && overview && (
        <Abschnitt
          titel="Überblick"
          testId="nutzung-ueberblick"
          erlaeuterung="Aktive Nutzer = Pseudonyme mit mindestens einem Seitenaufruf. Sitzung = ein Browser-Tab, Ende nach 30 Minuten Ruhe. Was weniger als fünf verschiedene Personen betrifft, wird nie ausgewiesen."
        >
          <UeberblickKacheln overview={overview} />
        </Abschnitt>
      )}

      {!laedt && !insights.error && !leer && insights.data && (
        <>
          <Abschnitt
            titel="Seiten"
            testId="nutzung-seiten"
            erlaeuterung="Routen als Muster, nie mit Nummern. Rücksprung = zurück zur vorigen Seite innerhalb von 10 Sekunden. Seiten, die für sich weniger als fünf Personen betreffen, erscheinen ohne Namen als Sammelzeile."
          >
            <SeitenTabelle rows={insights.data.usageRoutes} />
          </Abschnitt>

          {/* RITA-1523: der Vergleich zweier ausgelieferter Stände ist eine Aufschlüsselung der
              Seitenzeilen (dieselbe Reihenfolge) und steht deshalb direkt dahinter. Er bringt seine
              eigene Abfrage mit: die zwei Kennungen folgen erst aus der Ständeliste. */}
          <VersionsvergleichAbschnitt
            days={days}
            report={insights.data.versions}
            onListeNeuLaden={handleStaendeNeuLaden}
          />

          <Abschnitt
            titel="Funktionen"
            testId="nutzung-funktionen"
            erlaeuterung="Markierte Bedienelemente mit sprechendem Schlüssel je Route, unmarkierte nur mit ihrer Art und technischen Kennung. Eine Liste „nie genutzt“ gibt es bewusst nicht: seltene Nutzung unter fünf Personen ist aus Datenschutzgründen nicht von „nie“ zu unterscheiden."
          >
            <FunktionenTabelle klicks={insights.data.actionsClick.items} aktionen={insights.data.actionsAction.items} />
          </Abschnitt>

          {/* RITA-1520 (Phase 3b): Frust an der Oberfläche. Die beiden Arten stehen zwischen
              „Funktionen“ (was benutzt wird) und „Wege“ (wohin es geht) — sie sagen, wo das
              Benutzen hakt. */}
          <Abschnitt
            titel="Wutklicks und Totklicks"
            testId="nutzung-frustration"
            erlaeuterung="Wutklick = drei oder mehr Klicks auf dasselbe Ziel innerhalb einer Sekunde; gezählt wird ein Eintrag je Ausbruch und höchstens einer je Ziel und Minute, damit eine einzelne genervte Person keine Zeile bestimmt. Totklick = ein Klick auf etwas, das bedienbar aussieht, es aber nicht ist: ein deaktivierter Knopf, eine Zeigerhand oder ein markierter Bereich. Eingabefelder und das Markieren von Text zählen nicht mit. Die Schlüssel enthalten weder Nummern noch Texte, sondern nur den Ort im Aufbau der Seite. Was weniger als fünf Personen betrifft, erscheint ohne Namen als Sammelzeile."
          >
            <WutUndTotklickTabellen
              wutklicks={insights.data.actionsRage.items}
              totklicks={insights.data.actionsDead.items}
            />
          </Abschnitt>

          <Abschnitt
            titel="Wege"
            testId="nutzung-wege"
            erlaeuterung="Häufigste Übergänge zwischen Seiten und ihr Rücksprunganteil."
          >
            <WegeTabelle rows={insights.data.usageTransitions} />
          </Abschnitt>

          <Abschnitt
            titel="Serveroperationen"
            testId="nutzung-serveroperationen"
            erlaeuterung="Nur Operationsname, Art und Dauer; nie Parameter. Median und 95 % über mehrere Tage sind Näherungen aus Tages-Histogrammen; liegt das 95 % über der letzten Stufe, steht eine Textmarke statt einer Zahl. Antwortcodes zählen alle Antworten, nicht nur Fehler."
          >
            <ServeroperationenTabelle rows={insights.data.usageApiOperations} />
          </Abschnitt>

          {/* RITA-1515 (Phase 3a): Web Vitals aus dem Browser der Mitarbeiter. PRM lädt einmal und
              wechselt danach ohne Neuladen — LCP/TTFB gibt es deshalb nur je echtem Laden. */}
          <Abschnitt
            titel="Ladeleistung"
            testId="nutzung-ladeleistung"
            erlaeuterung="Wie schnell die Oberfläche lädt und reagiert, gemessen im Browser (Web Vitals). PRM lädt einmal und wechselt danach ohne Neuladen die Seite: LCP und TTFB entstehen nur bei einem echten Laden und stehen an der dabei geöffneten Seite (Anmeldeseite = „unbekannt“); INP und CLS beschreiben die Bedienung bis zum Ausblenden des Tabs und stehen an der Seite, auf der die Verzögerung bzw. der Sprung passierte. Median und 95 % sind Näherungen aus Tages-Histogrammen; das 95 % erscheint erst ab zehn Personen. Seiten mit weniger als fünf Personen bilden eine Sammelzeile."
          >
            <LadeleistungTabelle rows={insights.data.usageVitals} />
          </Abschnitt>

          <Abschnitt
            titel="Geräte und Darstellung"
            testId="nutzung-geraete"
            erlaeuterung="Anteile je Sitzung. Gruppen unter fünf Nutzern werden zusammengefasst; summieren sich die Anteile nicht auf 100 %, steht das unter der Liste."
          >
            <AnteilsListen
              device={insights.data.sessionsDevice}
              browser={insights.data.sessionsBrowser}
              theme={insights.data.sessionsTheme}
              layout={insights.data.sessionsLayout}
              viewport={insights.data.sessionsViewport}
            />
          </Abschnitt>

          <Abschnitt
            titel="Formulare und Suche"
            testId="nutzung-formulare"
            erlaeuterung="„Abgeschickt“ = an den Server gesendet und ohne Fehler beantwortet; Abbruch = geöffnet, aber ohne Absenden verlassen. Gezählt werden nur Feldnamen, nie Werte; Suchbegriffe werden gar nicht gespeichert."
          >
            <FormulareUndSuche report={insights.data.usageForms} />
          </Abschnitt>

          <FehlerAbschnitt
            days={days}
            report={fehlerAbfrage.data?.usageErrors}
            loading={fehlerAbfrage.loading}
            error={fehlerAbfrage.error}
            onErneut={() => {
              fehlerAbfrage.refetch().catch(() => undefined);
            }}
          />
        </>
      )}
    </>
  );
}
